import { useState } from "react";
import { Button, Checkbox, Dropdown, Input } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/es/table";

import "./ProjectColumnPicker.scss";
import { IProject } from "projects/models";

type Props = {
  /** The columns the user is allowed to choose from (role-accessible set). */
  columns: ColumnType<IProject>[];
  /** Keys of the columns currently shown. */
  value: string[];
  /** Called with the new set of visible column keys when the user toggles one. */
  onChange: (keys: string[]) => void;
};

export const ProjectColumnPicker = ({ columns, value, onChange }: Props) => {
  const [query, setQuery] = useState("");

  const toggle = (key: string, checked: boolean) => {
    const next = checked ? [...value, key] : value.filter((k) => k !== key);
    onChange(next);
  };

  // Don't let the user hide the final column — an empty table is never useful.
  const isLastVisible = (key: string) =>
    value.length === 1 && value.includes(key);

  const search = query.trim().toLowerCase();
  const filtered = search
    ? columns.filter((column) =>
        String(column.title).toLowerCase().includes(search)
      )
    : columns;

  const menu = (
    <div
      className='project-column-picker-menu'
      data-testid='projectColumnPickerMenu'
    >
      <div className='project-column-picker-search'>
        <Input
          allowClear
          size='small'
          placeholder='Search columns'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className='project-column-picker-list'>
        {filtered.map((column) => {
          const key = String(column.key);
          const checked = value.includes(key);
          return (
            <div key={key} className='project-column-picker-item'>
              <Checkbox
                checked={checked}
                disabled={checked && isLastVisible(key)}
                onChange={(e) => toggle(key, e.target.checked)}
              >
                {column.title as React.ReactNode}
              </Checkbox>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Dropdown
      trigger={["click"]}
      placement='bottomRight'
      dropdownRender={() => menu}
    >
      <Button icon={<SettingOutlined />} data-testid='projectColumnPicker'>
        Columns
      </Button>
    </Dropdown>
  );
};
